import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import config from '../config.js'
import useNesterTheme from '../components/nester/useNesterTheme'
import useNesterKeyboard, { KEYMAP } from '../components/nester/useNesterKeyboard'
import NesterStickyHeader from '../components/nester/NesterStickyHeader'
import NesterSetupAccordion from '../components/nester/NesterSetupAccordion'
import NesterTabs, { buildNesterTabs } from '../components/nester/NesterTabs'
import NesterDashboard from '../components/nester/NesterDashboard'
import NesterGroupDetail from '../components/nester/NesterGroupDetail'
import NesterPartsTable from '../components/nester/NesterPartsTable'
import NesterAiCoach from '../components/nester/NesterAiCoach'
import NesterExportsTab from '../components/nester/NesterExportsTab'
import NesterRemnantsPanel from '../components/nester/NesterRemnantsPanel'
import NesterCustomerHistory from '../components/nester/NesterCustomerHistory'
import { statusPill } from '../components/nester/theme'
import CreatePartsModal from '../components/nester/CreatePartsModal'
import CreateCutSheetsModal from '../components/nester/CreateCutSheetsModal'
import CreateQuoteModal from '../components/nester/CreateQuoteModal'
import { apiErrorMessage } from '../utils/apiError';

const NESTER = config.NESTER_API_URL
const API = config.API_BASE_URL

// FastAPI 400/422 responses ship as JSON {"detail": "..."}; surface that to
// operators instead of the raw JSON string. Used by every save flow that
// can hit the laser_tech_specs enforcement (project + material_group).
async function readErrorDetail(res) {
  const text = await res.text()
  try {
    const j = JSON.parse(text)
    return j.detail || j.message || text
  } catch { return text }
}

export default function DxfNesterPage() {
  const theme = useNesterTheme()
  const { colors, mode, toggle: toggleTheme } = theme

  // ── Project list
  const [projects, setProjects] = useState([])
  const [selectedProject, setSelectedProject] = useState(null)
  const [statusFilter, setStatusFilter] = useState('')
  const [listLoading, setListLoading] = useState(false)
  const [projectQuery, setProjectQuery] = useState('')

  // ── Project form
  const [projectName, setProjectName] = useState('')
  const [customerName, setCustomerName] = useState('')
  const [description, setDescription] = useState('')
  const [status, setStatus] = useState('draft')
  const [customerId, setCustomerId] = useState('')
  const [surfaceFinish, setSurfaceFinish] = useState('')

  // ── Material params
  const [materialType, setMaterialType] = useState('')
  const [thicknessDisplay, setThicknessDisplay] = useState('')
  const [thicknessIn, setThicknessIn] = useState('')
  const [sheetWidth, setSheetWidth] = useState('48')
  const [sheetLength, setSheetLength] = useState('96')
  const [feedrateIpm, setFeedrateIpm] = useState('')
  const [pierceTimeSec, setPierceTimeSec] = useState('')
  const [gasType, setGasType] = useState('')
  const [nozzleCleanPierces, setNozzleCleanPierces] = useState('50')
  const [nozzleCleanSec, setNozzleCleanSec] = useState('60')
  const [alignmentTimeSec, setAlignmentTimeSec] = useState('20')

  // ── Cost params
  const [customerSupplied, setCustomerSupplied] = useState(false)
  const [costPerSheet, setCostPerSheet] = useState('')
  const [costPerLb, setCostPerLb] = useState('')
  const [gasFlowCfh, setGasFlowCfh] = useState('')
  const [gasCostPerCf, setGasCostPerCf] = useState('')
  const [consumableRate, setConsumableRate] = useState('')

  // ── Reference data
  const [materialTypes, setMaterialTypes] = useState([])
  const [thicknesses, setThicknesses] = useState([])
  const [laserSpecs, setLaserSpecs] = useState([])
  const [customers, setCustomers] = useState([])
  const [surfaceFinishes, setSurfaceFinishes] = useState([])
  const [allMaterials, setAllMaterials] = useState([])
  const [allParts, setAllParts] = useState([])

  // ── Material groups + parts
  const [materialGroups, setMaterialGroups] = useState([])
  const [uploadGroupId, setUploadGroupId] = useState(null)
  const [parts, setParts] = useState([])
  const [uploading, setUploading] = useState(false)

  // ── Nest state
  const [nestResult, setNestResult] = useState(null)
  const [nesting, setNesting] = useState(false)
  const [commonLine, setCommonLine] = useState(false)
  const [partGap, setPartGap] = useState('0.25')
  const [edgeMargin, setEdgeMargin] = useState('0.5')
  const [shuttleTime, setShuttleTime] = useState('1')

  // ── UI state
  const [activeTab, setActiveTab] = useState('overview')
  const [highlightedPartId, setHighlightedPartId] = useState(null)
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)
  const [coachCount, setCoachCount] = useState(0)
  const [showKeymap, setShowKeymap] = useState(false)

  // ── Modals
  const [showCreateParts, setShowCreateParts] = useState(false)
  const [showCreateCutSheets, setShowCreateCutSheets] = useState(false)
  const [showCreateQuote, setShowCreateQuote] = useState(false)

  const fileInputRef = useRef(null)

  const fillResolutions = useMemo(() => {
    if (!nestResult) return {}
    const map = {}
    const collect = (summary) => {
      if (!summary) return
      summary.forEach(p => { if (p.fill_sheet) map[p.part_id] = p.quantity })
    }
    collect(nestResult.parts_summary)
    if (Array.isArray(nestResult.group_results)) {
      nestResult.group_results.forEach(g => collect(g.parts_summary))
    }
    return map
  }, [nestResult])

  useEffect(() => { loadProjects(); loadMaterialData() }, []) // eslint-disable-line
  useEffect(() => { loadProjects() }, [statusFilter]) // eslint-disable-line

  async function loadProjects() {
    setListLoading(true)
    try {
      const url = statusFilter ? `${NESTER}/projects?status=${statusFilter}` : `${NESTER}/projects`
      const res = await fetch(url)
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      setProjects(await res.json())
    } catch (e) { setError(e.message) }
    setListLoading(false)
  }

  async function loadMaterialData() {
    try {
      const [typesRes, thicknessRes, specsRes, custRes, cwtRes, matsRes, partsRes] = await Promise.all([
        fetch(`${API}/materials/types`),
        fetch(`${API}/material_thicknesses`),
        fetch(`${API}/laser_specs`),
        fetch(`${API}/customers`),
        fetch(`${API}/cwt_pricing_options`),
        fetch(`${API}/materials`),
        fetch(`${API}/parts`),
      ])
      if (typesRes.ok) setMaterialTypes(await typesRes.json())
      if (thicknessRes.ok) setThicknesses(await thicknessRes.json())
      if (specsRes.ok) setLaserSpecs(await specsRes.json())
      if (custRes.ok) setCustomers((await custRes.json()).sort((a, b) => a.name.localeCompare(b.name)))
      if (cwtRes.ok) {
        const opts = await cwtRes.json()
        setSurfaceFinishes(opts.surface_finishes || [])
      }
      if (matsRes.ok) setAllMaterials(await matsRes.json())
      if (partsRes.ok) setAllParts(await partsRes.json())
    } catch (e) { console.error('Failed to load material data:', e) }
  }

  function selectProject(project) {
    setSelectedProject(project)
    setProjectName(project.project_name)
    setCustomerName(project.customer_name || '')
    setCustomerId(project.customer_id?.toString() || '')
    setDescription(project.description || '')
    setStatus(project.status)
    setSurfaceFinish(project.surface_finish || '')
    setMaterialType(project.material_type || '')
    setThicknessDisplay(project.thickness_display || '')
    setThicknessIn(project.thickness_in?.toString() || '')
    setSheetWidth(project.sheet_width?.toString() || '48')
    setSheetLength(project.sheet_length?.toString() || '96')
    setFeedrateIpm(project.feedrate_ipm?.toString() || '')
    setPierceTimeSec(project.pierce_time_sec?.toString() || '')
    setGasType(project.gas_type || '')
    setNozzleCleanPierces(project.nozzle_clean_pierces?.toString() || '50')
    setNozzleCleanSec(project.nozzle_clean_sec?.toString() || '60')
    setAlignmentTimeSec(project.alignment_time_sec?.toString() || '20')
    setCustomerSupplied(project.customer_supplied_material || false)
    setCostPerSheet(project.cost_per_sheet?.toString() || '')
    setCostPerLb(project.cost_per_lb?.toString() || '')
    setGasFlowCfh(project.gas_flow_cfh?.toString() || '')
    setGasCostPerCf(project.gas_cost_per_cf?.toString() || '')
    setConsumableRate(project.consumable_rate_per_hour?.toString() || '')
    if (project.thickness_in) {
      setPartGap(project.thickness_in.toString())
      setEdgeMargin(project.thickness_in.toString())
    } else {
      setPartGap('0.25'); setEdgeMargin('0.5')
    }
    const groups = project.material_groups || []
    setMaterialGroups(groups)
    setUploadGroupId(groups.length === 1 ? groups[0].group_id : null)
    setNestResult(null)
    setHighlightedPartId(null)
    setActiveTab('overview')
    loadParts(project.project_id)
    loadLatestNest(project.project_id)

    const hasMissingCosts = !project.gas_cost_per_cf || !project.consumable_rate_per_hour ||
      (!project.customer_supplied_material && !project.cost_per_sheet)
    if (project.material_type && project.thickness_display && hasMissingCosts) {
      const params = new URLSearchParams({
        material_type: project.material_type,
        thickness_display: project.thickness_display,
      })
      if (project.sheet_width) params.append('sheet_width', project.sheet_width)
      if (project.sheet_length) params.append('sheet_length', project.sheet_length)
      if (project.surface_finish) params.append('surface_finish', project.surface_finish)
      fetch(`${NESTER}/cost_lookup?${params}`)
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (!data) return
          if (data.gas_cost_per_cf && !project.gas_cost_per_cf) setGasCostPerCf(data.gas_cost_per_cf.toString())
          if (data.consumable_rate_per_hour && !project.consumable_rate_per_hour) setConsumableRate(data.consumable_rate_per_hour.toString())
          if (!project.customer_supplied_material) {
            if (data.cost_per_sheet && !project.cost_per_sheet) setCostPerSheet(data.cost_per_sheet.toString())
            if (data.cost_per_lb && !project.cost_per_lb) setCostPerLb(data.cost_per_lb.toString())
          }
          if (data.gas_flow_cfh && !project.gas_flow_cfh) setGasFlowCfh(data.gas_flow_cfh.toString())
        })
        .catch(() => {})
    }
  }

  async function loadParts(projectId) {
    try {
      const res = await fetch(`${NESTER}/projects/${projectId}/parts`)
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      const loaded = await res.json()
      setParts(loaded)
      setCommonLine(loaded.length > 0 && loaded.every(p => p.common_line))
    } catch (e) { setError(e.message) }
  }

  async function loadLatestNest(projectId) {
    try {
      const res = await fetch(`${NESTER}/projects/${projectId}/nests?limit=50`)
      if (!res.ok) return
      const nests = await res.json()
      if (nests.length === 0) return

      // Dedupe by material_group_id — keep only the most recent nest per group.
      // The /nests endpoint returns rows ordered by created_at DESC, but if the
      // user re-runs the nest, every prior run leaves stale rows that would
      // otherwise be summed into the totals. Iterate in API order and take the
      // first occurrence of each group_id.
      const seen = new Set()
      const dedupedGroupNests = []
      for (const n of nests) {
        if (n.material_group_id == null) continue
        if (seen.has(n.material_group_id)) continue
        seen.add(n.material_group_id)
        dedupedGroupNests.push(n)
      }

      if (dedupedGroupNests.length > 1) {
        let totalSheets = 0, totalTimeSec = 0, totalCost = 0
        for (const n of dedupedGroupNests) {
          totalSheets += n.sheets_needed || 0
          totalTimeSec += n.detailed_estimate?.total_sec || n.est_total_time_sec || 0
          totalCost += n.cost_summary?.total_cost || 0
        }
        setNestResult({
          group_results: dedupedGroupNests,
          total_sheets: totalSheets,
          total_time_sec: totalTimeSec,
          total_cost: totalCost,
        })
      } else if (dedupedGroupNests.length === 1) {
        setNestResult(dedupedGroupNests[0])
      } else {
        // Single-group legacy project — no material_group_id on rows
        setNestResult(nests[0])
      }
    } catch (e) { console.warn('Failed to load nests:', e) }
  }

  function resetForm() {
    setSelectedProject(null)
    setProjectName(''); setCustomerName(''); setCustomerId(''); setDescription(''); setStatus('draft')
    setSurfaceFinish(''); setMaterialType(''); setThicknessDisplay(''); setThicknessIn('')
    setSheetWidth('48'); setSheetLength('96'); setFeedrateIpm(''); setPierceTimeSec(''); setGasType('')
    setNozzleCleanPierces('50'); setNozzleCleanSec('60'); setAlignmentTimeSec('20')
    setCustomerSupplied(false); setCostPerSheet(''); setCostPerLb('')
    setGasFlowCfh(''); setGasCostPerCf(''); setConsumableRate('')
    setMaterialGroups([]); setUploadGroupId(null); setParts([])
    setNestResult(null); setHighlightedPartId(null); setError(null)
    setActiveTab('overview')
  }

  const handleThicknessChange = useCallback((display, inches) => {
    setThicknessDisplay(display); setThicknessIn(inches)
    if (inches) { setPartGap(inches); setEdgeMargin(inches) }
    if (materialType && display) {
      const spec = laserSpecs.find(s =>
        s.material_type?.toLowerCase() === materialType.toLowerCase() &&
        s.display_thickness === display
      )
      if (spec) {
        setFeedrateIpm(spec.feedrate_ipm?.toString() || '')
        setPierceTimeSec(spec.pierce_time_sec?.toString() || '')
        setGasType(spec.gas_type || '')
        if (spec.gas_flow_cfh) setGasFlowCfh(spec.gas_flow_cfh.toString())
      }
      const params = new URLSearchParams({ material_type: materialType, thickness_display: display })
      if (sheetWidth) params.append('sheet_width', sheetWidth)
      if (sheetLength) params.append('sheet_length', sheetLength)
      if (surfaceFinish) params.append('surface_finish', surfaceFinish)
      fetch(`${NESTER}/cost_lookup?${params}`)
        .then(r => r.ok ? r.json() : null)
        .then(data => {
          if (!data) return
          if (data.gas_flow_cfh && !spec?.gas_flow_cfh) setGasFlowCfh(data.gas_flow_cfh.toString())
          if (data.gas_cost_per_cf) setGasCostPerCf(data.gas_cost_per_cf.toString())
          if (data.consumable_rate_per_hour) setConsumableRate(data.consumable_rate_per_hour.toString())
          if (data.cost_per_sheet) setCostPerSheet(data.cost_per_sheet.toString())
          if (data.cost_per_lb) setCostPerLb(data.cost_per_lb.toString())
        })
        .catch(() => {})
    }
  }, [materialType, laserSpecs, sheetWidth, sheetLength, surfaceFinish])

  async function createProject() {
    if (!projectName.trim()) return setError('Project name is required')
    setSaving(true); setError(null)
    try {
      const body = buildProjectPayload({
        projectName, customerName, customerId, description, status, surfaceFinish,
        materialType, thicknessDisplay, thicknessIn, sheetWidth, sheetLength,
        feedrateIpm, pierceTimeSec, gasType, nozzleCleanPierces, nozzleCleanSec,
        customerSupplied, costPerSheet, costPerLb, gasFlowCfh, gasCostPerCf, consumableRate,
      })
      const res = await fetch(`${NESTER}/projects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!res.ok) throw new Error(await readErrorDetail(res))
      const created = await res.json()
      setSelectedProject(created)
      loadProjects()
    } catch (e) { setError(e.message) }
    setSaving(false)
  }

  async function saveProject() {
    if (!selectedProject) return createProject()
    setSaving(true); setError(null)
    try {
      const body = buildProjectPayload({
        projectName, customerName, customerId, description, status, surfaceFinish,
        materialType, thicknessDisplay, thicknessIn, sheetWidth, sheetLength,
        feedrateIpm, pierceTimeSec, gasType, nozzleCleanPierces, nozzleCleanSec,
        customerSupplied, costPerSheet, costPerLb, gasFlowCfh, gasCostPerCf, consumableRate,
      })
      const res = await fetch(`${NESTER}/projects/${selectedProject.project_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!res.ok) throw new Error(await readErrorDetail(res))
      const updated = await res.json()
      setSelectedProject(updated)
      loadProjects()
    } catch (e) { setError(e.message) }
    setSaving(false)
  }

  async function deleteProject() {
    if (!selectedProject) return
    if (!window.confirm(`Delete project "${selectedProject.project_name}"?`)) return
    try {
      const res = await fetch(`${NESTER}/projects/${selectedProject.project_id}`, { method: 'DELETE' })
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      resetForm(); loadProjects()
    } catch (e) { setError(e.message) }
  }

  async function handleDxfUpload(e) {
    const files = e.target.files
    if (!files?.length || !selectedProject) return
    setUploading(true); setError(null)
    for (const file of files) {
      try {
        const fd = new FormData()
        fd.append('file', file)
        let uploadUrl = `${NESTER}/projects/${selectedProject.project_id}/parts/upload`
        if (uploadGroupId) uploadUrl += `?material_group_id=${uploadGroupId}`
        const res = await fetch(uploadUrl, { method: 'POST', body: fd })
        if (!res.ok) {
          const errText = await res.text()
          setError(`Failed to upload ${file.name}: ${errText}`)
          continue
        }
      } catch (err) {
        setError(`Upload error for ${file.name}: ${err.message}`)
      }
    }
    await loadParts(selectedProject.project_id)
    setUploading(false)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }

  async function updatePartQuantity(partId, qty) {
    if (qty < 1) return
    await updatePartField(partId, { quantity: qty })
  }

  async function updatePartField(partId, fields) {
    try {
      await fetch(`${NESTER}/parts/${partId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(fields),
      })
      setParts(prev => prev.map(p => p.part_id === partId ? { ...p, ...fields } : p))
    } catch (e) { setError(e.message) }
  }

  async function batchUpdateParts(updates) {
    if (!selectedProject) return
    try {
      const res = await fetch(`${NESTER}/projects/${selectedProject.project_id}/parts/batch`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ updates }),
      })
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      await loadParts(selectedProject.project_id)
    } catch (e) { setError(e.message) }
  }

  async function deletePart(partId) {
    try {
      await fetch(`${NESTER}/parts/${partId}`, { method: 'DELETE' })
      setParts(prev => prev.filter(p => p.part_id !== partId))
    } catch (e) { setError(e.message) }
  }

  async function runNest() {
    if (!selectedProject) return
    setNesting(true); setError(null)
    try {
      const saveRes = await fetch(`${NESTER}/projects/${selectedProject.project_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(buildProjectPayload({
          projectName, customerName, customerId, description, status, surfaceFinish,
          materialType, thicknessDisplay, thicknessIn, sheetWidth, sheetLength,
          feedrateIpm, pierceTimeSec, gasType, nozzleCleanPierces, nozzleCleanSec,
          customerSupplied, costPerSheet, costPerLb, gasFlowCfh, gasCostPerCf, consumableRate,
        })),
      })
      if (!saveRes.ok) throw new Error('Failed to save project: ' + await saveRes.text())
      setSelectedProject(await saveRes.json())

      const nestBody = {
        shuttle_time_min: parseFloat(shuttleTime) || 1.0,
        common_line: commonLine,
        nozzle_clean_pierces: nozzleCleanPierces ? parseInt(nozzleCleanPierces) : null,
        nozzle_clean_sec: nozzleCleanSec ? parseFloat(nozzleCleanSec) : null,
        alignment_time_sec: alignmentTimeSec ? parseFloat(alignmentTimeSec) : 20.0,
      }
      if (materialGroups.length === 0) {
        nestBody.part_gap = parseFloat(partGap) || 0.25
        nestBody.edge_margin = parseFloat(edgeMargin) || 0.5
      }
      const res = await fetch(`${NESTER}/projects/${selectedProject.project_id}/nest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(nestBody),
      })
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      setNestResult(await res.json())
      setActiveTab('overview')
    } catch (e) { setError(e.message) }
    setNesting(false)
  }

  async function convertToGroups() {
    if (!selectedProject) return
    setError(null)
    try {
      const body = {
        material_type: materialType || null,
        thickness_display: thicknessDisplay || null,
        thickness_in: thicknessIn ? parseFloat(thicknessIn) : null,
        sheet_width: sheetWidth ? parseFloat(sheetWidth) : null,
        sheet_length: sheetLength ? parseFloat(sheetLength) : null,
        feedrate_ipm: feedrateIpm ? parseFloat(feedrateIpm) : null,
        pierce_time_sec: pierceTimeSec ? parseFloat(pierceTimeSec) : null,
        gas_type: gasType || null,
        nozzle_clean_pierces: nozzleCleanPierces ? parseInt(nozzleCleanPierces) : null,
        nozzle_clean_sec: nozzleCleanSec ? parseFloat(nozzleCleanSec) : null,
        customer_supplied_material: customerSupplied,
        cost_per_sheet: customerSupplied ? null : (costPerSheet ? parseFloat(costPerSheet) : null),
        cost_per_lb: customerSupplied ? null : (costPerLb ? parseFloat(costPerLb) : null),
        gas_flow_cfh: gasFlowCfh ? parseFloat(gasFlowCfh) : null,
        gas_cost_per_cf: gasCostPerCf ? parseFloat(gasCostPerCf) : null,
        consumable_rate_per_hour: consumableRate ? parseFloat(consumableRate) : null,
        part_gap: partGap ? parseFloat(partGap) : null,
        edge_margin: edgeMargin ? parseFloat(edgeMargin) : null,
      }
      const res = await fetch(`${NESTER}/projects/${selectedProject.project_id}/material_groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      if (!res.ok) throw new Error(await readErrorDetail(res))
      const group = await res.json()
      for (const part of parts) {
        await fetch(`${NESTER}/parts/${part.part_id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ material_group_id: group.group_id }),
        })
      }
      group.parts_count = parts.length
      setMaterialGroups([group])
      setUploadGroupId(group.group_id)
      setParts(parts.map(p => ({ ...p, material_group_id: group.group_id })))
    } catch (e) { setError(e.message) }
  }

  function handleGroupsChange(newGroups) {
    setMaterialGroups(newGroups)
    const remainingIds = new Set(newGroups.map(g => g.group_id))
    setParts(prev => prev.map(p =>
      p.material_group_id && !remainingIds.has(p.material_group_id)
        ? { ...p, material_group_id: null }
        : p
    ))
    if (newGroups.length === 1) setUploadGroupId(newGroups[0].group_id)
    else if (newGroups.length === 0) setUploadGroupId(null)
  }

  async function applyCoachAction(action) {
    if (!action) return
    try {
      if (action.kind === 'patch_part' && action.part_id) {
        await updatePartField(action.part_id, action.patch || {})
      } else if (action.kind === 'batch_patch_parts' && Array.isArray(action.updates)) {
        await batchUpdateParts(action.updates)
      } else if (action.kind === 'patch_project' && action.patch) {
        const res = await fetch(`${NESTER}/projects/${selectedProject.project_id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(action.patch),
        })
        if (res.ok) setSelectedProject(await res.json())
      } else if (action.kind === 'rerun_nest') {
        await runNest()
      }
    } catch (e) {
      setError('Failed to apply: ' + e.message)
    }
  }

  // ── Tabs
  const visibleGroups = nestResult?.group_results || (nestResult ? [nestResult] : [])
  const tabs = useMemo(() => buildNesterTabs(nestResult, materialGroups, {
    partsCount: parts.length || undefined,
    showCoach: !!selectedProject,
    showVersions: false,
    showRemnants: !!selectedProject,
  }), [nestResult, materialGroups, parts.length, selectedProject])

  useEffect(() => {
    if (!tabs.find(t => t.id === activeTab)) setActiveTab(tabs[0]?.id || 'overview')
  }, [tabs]) // eslint-disable-line

  useNesterKeyboard({
    tabs,
    setActiveTab,
    onRunNest: selectedProject && parts.length > 0 ? runNest : null,
    onSave: selectedProject ? saveProject : createProject,
    onNewProject: resetForm,
    onClearHighlight: () => { setHighlightedPartId(null); setShowKeymap(false) },
    onToggleKeymap: () => setShowKeymap(v => !v),
  })

  // Filter projects by query
  const displayedProjects = useMemo(() => {
    if (!projectQuery.trim()) return projects
    const q = projectQuery.toLowerCase()
    return projects.filter(p =>
      (p.project_name || '').toLowerCase().includes(q)
      || (p.customer_name || '').toLowerCase().includes(q)
      || (p.material_type || '').toLowerCase().includes(q)
    )
  }, [projects, projectQuery])

  async function exportNestFile(format, nestId) {
    if (!nestId) return
    try {
      const res = await fetch(`${NESTER}/nests/${nestId}/${format}`)
      if (!res.ok) throw new Error(await apiErrorMessage(res));
      const blob = await res.blob()
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `nest-${nestId}.${format}`
      document.body.appendChild(a); a.click(); a.remove()
      URL.revokeObjectURL(url)
    } catch (e) { setError(e.message) }
  }

  return (
    <div style={{
      display: 'flex', gap: 12,
      height: 'calc(100vh - 120px)',
      background: colors.bg,
      color: colors.text,
    }}>
      {/* LEFT PANEL — Project List */}
      <div style={{
        width: 280, minWidth: 280,
        background: colors.panel,
        borderRadius: 8, border: `1px solid ${colors.border}`,
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        boxShadow: colors.shadow,
      }}>
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          padding: '10px 12px',
          borderBottom: `1px solid ${colors.border}`,
          background: colors.panelAlt,
        }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: colors.text }}>
            Projects
            <span style={{ color: colors.textMuted, fontWeight: 500, marginLeft: 6 }}>
              {projects.length}
            </span>
          </div>
          <button onClick={resetForm}
            style={{
              padding: '4px 12px', fontSize: 12, fontWeight: 700,
              border: 'none', borderRadius: 4,
              background: colors.accent, color: '#fff', cursor: 'pointer',
            }}>+ New</button>
        </div>
        <div style={{ padding: '8px 8px 4px' }}>
          <input value={projectQuery}
            onChange={e => setProjectQuery(e.target.value)}
            placeholder="🔍 Search projects…"
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '5px 8px', fontSize: 12,
              border: `1px solid ${colors.border}`, borderRadius: 4,
              background: colors.panel, color: colors.text,
            }} />
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
            style={{
              width: '100%', boxSizing: 'border-box', marginTop: 6,
              padding: '5px 8px', fontSize: 12,
              border: `1px solid ${colors.border}`, borderRadius: 4,
              background: colors.panel, color: colors.text,
            }}>
            <option value="">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="quoted">Quoted</option>
            <option value="won">Won</option>
            <option value="lost">Lost</option>
          </select>
        </div>
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {listLoading && <div style={{ padding: 14, color: colors.textMuted, fontSize: 12 }}>Loading…</div>}
          {displayedProjects.map(p => {
            const pill = statusPill(p.status, mode)
            const active = selectedProject?.project_id === p.project_id
            return (
              <div key={p.project_id}
                style={{
                  padding: '8px 12px',
                  borderBottom: `1px solid ${colors.border}`,
                  background: active ? colors.accentSoft : 'transparent',
                  borderLeft: active ? `3px solid ${colors.accent}` : '3px solid transparent',
                  cursor: 'pointer', transition: 'background 0.12s',
                }}
                onClick={() => selectProject(p)}>
                <div style={{ fontWeight: 700, fontSize: 13, color: colors.text }}>{p.project_name}</div>
                <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 2, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    flex: '1 1 auto', minWidth: 0,
                  }}>
                    {p.customer_name || 'No customer'}
                  </span>
                  <span style={{
                    padding: '1px 6px', borderRadius: 3, fontSize: 10,
                    background: pill.bg, color: pill.fg, fontWeight: 700,
                    flexShrink: 0,
                  }}>{pill.label}</span>
                </div>
                <div style={{ fontSize: 10, color: colors.textFaint, marginTop: 2 }}>
                  {p.parts_count || 0} parts · {p.material_type || 'No material'}
                </div>
              </div>
            )
          })}
          {!listLoading && displayedProjects.length === 0 && (
            <div style={{ padding: 14, color: colors.textMuted, fontSize: 12, textAlign: 'center' }}>
              {projectQuery ? 'No projects match search' : 'No projects yet'}
            </div>
          )}
        </div>
      </div>

      {/* RIGHT PANEL */}
      <div style={{ flex: 1, overflowY: 'auto', paddingRight: 4 }}>
        <NesterStickyHeader
          colors={colors}
          mode={mode}
          project={selectedProject}
          nestResult={nestResult}
          status={status}
          parts={parts}
          groups={materialGroups}
          onToggleTheme={toggleTheme}
          onRunNest={parts.length > 0 ? runNest : null}
          onSave={saveProject}
          nesting={nesting}
          saving={saving}
          activeTab={activeTab}
          onTabJump={(id) => setActiveTab(id)}
        />

        {error && (
          <div style={{
            padding: 10, marginBottom: 8,
            background: colors.dangerSoft, border: `1px solid ${colors.danger}`,
            borderRadius: 6, color: colors.danger, fontSize: 13,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <span>{error}</span>
            <button onClick={() => setError(null)}
              style={{ background: 'none', border: 'none', color: colors.danger, cursor: 'pointer', fontSize: 16, fontWeight: 700 }}>×</button>
          </div>
        )}

        <NesterSetupAccordion
          colors={colors} mode={mode}
          onError={setError}
          projectName={projectName} setProjectName={setProjectName}
          customerId={customerId} setCustomerId={setCustomerId}
          customerName={customerName} setCustomerName={setCustomerName}
          customers={customers}
          status={status} setStatus={setStatus}
          description={description} setDescription={setDescription}
          materialType={materialType} setMaterialType={setMaterialType}
          surfaceFinish={surfaceFinish} setSurfaceFinish={setSurfaceFinish}
          thicknessDisplay={thicknessDisplay} thicknessIn={thicknessIn}
          handleThicknessChange={handleThicknessChange}
          sheetWidth={sheetWidth} setSheetWidth={setSheetWidth}
          sheetLength={sheetLength} setSheetLength={setSheetLength}
          feedrateIpm={feedrateIpm} setFeedrateIpm={setFeedrateIpm}
          pierceTimeSec={pierceTimeSec} setPierceTimeSec={setPierceTimeSec}
          gasType={gasType} setGasType={setGasType}
          nozzleCleanPierces={nozzleCleanPierces} setNozzleCleanPierces={setNozzleCleanPierces}
          nozzleCleanSec={nozzleCleanSec} setNozzleCleanSec={setNozzleCleanSec}
          alignmentTimeSec={alignmentTimeSec} setAlignmentTimeSec={setAlignmentTimeSec}
          customerSupplied={customerSupplied} setCustomerSupplied={setCustomerSupplied}
          costPerSheet={costPerSheet} setCostPerSheet={setCostPerSheet}
          costPerLb={costPerLb} setCostPerLb={setCostPerLb}
          gasFlowCfh={gasFlowCfh} setGasFlowCfh={setGasFlowCfh}
          gasCostPerCf={gasCostPerCf} setGasCostPerCf={setGasCostPerCf}
          consumableRate={consumableRate} setConsumableRate={setConsumableRate}
          materialTypes={materialTypes} thicknesses={thicknesses}
          surfaceFinishes={surfaceFinishes} laserSpecs={laserSpecs}
          materialGroups={materialGroups}
          handleGroupsChange={handleGroupsChange}
          convertToGroups={convertToGroups}
          parts={parts} selectedProject={selectedProject}
          uploading={uploading} uploadGroupId={uploadGroupId} setUploadGroupId={setUploadGroupId}
          handleDxfUpload={handleDxfUpload} fileInputRef={fileInputRef}
          partGap={partGap} setPartGap={setPartGap}
          edgeMargin={edgeMargin} setEdgeMargin={setEdgeMargin}
          fillResolutions={fillResolutions}
          updatePartField={updatePartField} updatePartQuantity={updatePartQuantity}
          deletePart={deletePart}
          hasNestResult={!!nestResult}
        />

        {/* Nest controls bar */}
        {selectedProject && parts.length > 0 && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
            padding: '8px 12px', marginBottom: 8,
            background: colors.panel,
            border: `1px solid ${colors.border}`, borderRadius: 8,
            boxShadow: colors.shadow,
          }}>
            <button onClick={runNest} disabled={nesting}
              style={{
                padding: '6px 16px', fontSize: 13, fontWeight: 700,
                border: 'none', borderRadius: 4,
                background: colors.success, color: '#fff',
                cursor: nesting ? 'wait' : 'pointer',
              }}>
              {nesting ? 'Nesting…' : '▶ Generate Nest'}
              <span style={{ opacity: 0.7, marginLeft: 6, fontSize: 10, fontWeight: 500 }}>G</span>
            </button>
            {materialGroups.length === 0 && (
              <>
                <label style={labelStyle(colors)}>
                  Part Gap
                  <input type="number" value={partGap} onChange={e => setPartGap(e.target.value)}
                    step="0.0625" min="0" max="1" style={miniInput(colors)} />
                </label>
                <label style={labelStyle(colors)}>
                  Edge Margin
                  <input type="number" value={edgeMargin} onChange={e => setEdgeMargin(e.target.value)}
                    step="0.0625" min="0" max="2" style={miniInput(colors)} />
                </label>
              </>
            )}
            <label style={labelStyle(colors)}>
              Shuttle
              <input type="number" value={shuttleTime} onChange={e => setShuttleTime(e.target.value)}
                step="0.5" min="0" max="10" style={miniInput(colors)} />
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, color: colors.text, cursor: 'pointer' }}
              title="Enable common-line cutting for ALL parts. Per-part CL flags still apply when this is off.">
              <input type="checkbox" checked={commonLine} onChange={e => {
                const checked = e.target.checked
                setCommonLine(checked)
                parts.forEach(p => {
                  if (!!p.common_line !== checked) updatePartField(p.part_id, { common_line: checked })
                })
              }} />
              All CL
              {!commonLine && parts.some(p => p.common_line) && (
                <span style={{ fontSize: 11, color: colors.success, fontWeight: 700 }}>
                  ({parts.filter(p => p.common_line).length})
                </span>
              )}
            </label>
            <div style={{ flex: 1 }} />
            {selectedProject && (
              <button onClick={deleteProject}
                style={{
                  padding: '4px 12px', fontSize: 11, fontWeight: 600,
                  border: `1px solid ${colors.danger}`, color: colors.danger,
                  background: 'transparent', borderRadius: 4, cursor: 'pointer',
                }}>
                Delete project
              </button>
            )}
          </div>
        )}

        {/* Results — tabbed */}
        {(selectedProject || nestResult) && (
          <div style={{
            background: colors.panel,
            border: `1px solid ${colors.border}`,
            borderRadius: 8, overflow: 'hidden', marginBottom: 12,
            boxShadow: colors.shadow,
          }}>
            <NesterTabs
              colors={colors}
              tabs={tabs}
              activeTab={activeTab}
              onChange={setActiveTab}
              badges={{ coach: coachCount > 0 ? coachCount : null }}
            />
            <div>
              {activeTab === 'overview' && (
                <>
                  {customerId && (
                    <div style={{ padding: '14px 16px 0' }}>
                      <NesterCustomerHistory
                        colors={colors}
                        customerId={parseInt(customerId)}
                        customerName={customerName}
                      />
                    </div>
                  )}
                  <NesterDashboard
                    colors={colors}
                    nestResult={nestResult}
                    materialGroups={materialGroups}
                    parts={parts}
                    onNavigateToGroup={(tabId) => setActiveTab(tabId)}
                    onJumpToCoach={() => setActiveTab('coach')}
                    coachSuggestionCount={coachCount}
                  />
                </>
              )}
              {activeTab.startsWith('group-') && renderGroupTab()}
              {activeTab === 'parts' && (
                <NesterPartsTable
                  colors={colors}
                  parts={parts}
                  materialGroups={materialGroups}
                  highlightedPartId={highlightedPartId}
                  onHighlightPart={setHighlightedPartId}
                  onUpdateField={updatePartField}
                  onUpdateQuantity={updatePartQuantity}
                  onDeletePart={deletePart}
                  onBatchUpdate={batchUpdateParts}
                  fillResolutions={fillResolutions}
                />
              )}
              {activeTab === 'coach' && (
                <NesterAiCoach
                  colors={colors}
                  project={selectedProject}
                  nestResult={nestResult}
                  onApplyAction={applyCoachAction}
                  onSetSuggestionCount={setCoachCount}
                />
              )}
              {activeTab === 'remnants' && (
                <NesterRemnantsPanel
                  colors={colors}
                  project={selectedProject}
                  materialGroups={materialGroups}
                  parts={parts}
                  nestResult={nestResult}
                />
              )}
              {activeTab === 'exports' && (
                <NesterExportsTab
                  colors={colors}
                  project={selectedProject}
                  nestResult={nestResult}
                  onOpenCreateParts={() => setShowCreateParts(true)}
                  onOpenCreateCutSheets={() => setShowCreateCutSheets(true)}
                  onOpenCreateQuote={() => setShowCreateQuote(true)}
                  canCreateQuote={!!customerId && !!nestResult}
                />
              )}
            </div>
          </div>
        )}

        {!selectedProject && !nestResult && (
          <div style={{
            padding: '60px 20px', textAlign: 'center',
            background: colors.panel,
            border: `1px dashed ${colors.border}`, borderRadius: 8,
            boxShadow: colors.shadow, color: colors.textMuted,
          }}>
            <div style={{ fontSize: 40, opacity: 0.3 }}>◇</div>
            <div style={{ marginTop: 10, fontSize: 14, color: colors.text }}>
              Select a project from the left or click <strong>+ New</strong> to start quoting.
            </div>
            <div style={{ marginTop: 16, fontSize: 12, color: colors.textMuted }}>
              Press <kbd style={kbd(colors)}>?</kbd> any time to see keyboard shortcuts.
            </div>
          </div>
        )}
      </div>

      {/* Keymap overlay */}
      {showKeymap && (
        <div onClick={() => setShowKeymap(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 1000,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
          <div onClick={e => e.stopPropagation()}
            style={{
              background: colors.panel, color: colors.text,
              borderRadius: 8, padding: 20, minWidth: 340,
              boxShadow: colors.shadowLg,
            }}>
            <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>Keyboard Shortcuts</div>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {KEYMAP.map((row, i) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${colors.border}` }}>
                    <td style={{ padding: '6px 0' }}>
                      {row.keys.map((k, j) => (
                        <span key={j}>
                          {j > 0 && <span style={{ color: colors.textFaint, margin: '0 4px' }}>+</span>}
                          <kbd style={kbd(colors)}>{k}</kbd>
                        </span>
                      ))}
                    </td>
                    <td style={{ padding: '6px 0', fontSize: 13 }}>{row.action}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: 14, textAlign: 'right' }}>
              <button onClick={() => setShowKeymap(false)}
                style={{
                  padding: '5px 14px', borderRadius: 4,
                  background: colors.accent, color: '#fff',
                  border: 'none', fontWeight: 600, cursor: 'pointer',
                }}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Modals */}
      {showCreateParts && (
        <CreatePartsModal
          show={showCreateParts} onClose={() => setShowCreateParts(false)}
          parts={parts} project={selectedProject}
          nestResult={nestResult}
          materialGroups={materialGroups} thicknesses={thicknesses}
          customers={customers} surfaceFinishes={surfaceFinishes}
          projectSurfaceFinish={surfaceFinish} projectCustomerId={customerId}
          projectMaterialType={materialType} projectThicknessDisplay={thicknessDisplay}
          projectThicknessIn={thicknessIn}
          projectFeedrateIpm={feedrateIpm} projectPierceTimeSec={pierceTimeSec}
        />
      )}
      {showCreateCutSheets && (
        <CreateCutSheetsModal
          show={showCreateCutSheets} onClose={() => setShowCreateCutSheets(false)}
          nestResult={nestResult} project={selectedProject}
          materialGroups={materialGroups}
          allMaterials={allMaterials} allParts={allParts}
          customers={customers} thicknesses={thicknesses}
          projectMaterialType={materialType} projectThicknessDisplay={thicknessDisplay}
          projectThicknessIn={thicknessIn} projectSurfaceFinish={surfaceFinish}
          projectCustomerId={customerId}
          projectSheetWidth={sheetWidth} projectSheetLength={sheetLength}
        />
      )}
      {showCreateQuote && (
        <CreateQuoteModal
          show={showCreateQuote} onClose={() => setShowCreateQuote(false)}
          nestResult={nestResult} project={selectedProject}
          materialGroups={materialGroups} parts={parts}
          allMaterials={allMaterials} allParts={allParts}
          thicknesses={thicknesses} surfaceFinishes={surfaceFinishes}
          customers={customers}
          projectMaterialType={materialType} projectThicknessDisplay={thicknessDisplay}
          projectSurfaceFinish={surfaceFinish} projectCustomerId={customerId}
          projectSheetWidth={sheetWidth} projectSheetLength={sheetLength}
          projectCustomerSupplied={customerSupplied}
          projectCostPerSheet={costPerSheet} projectCostPerLb={costPerLb}
          projectThicknessIn={thicknessIn}
        />
      )}
    </div>
  )

  function renderGroupTab() {
    if (activeTab === 'group-single') {
      return (
        <NesterGroupDetail
          colors={colors}
          mode={mode}
          group={nestResult}
          mg={null}
          idx={0}
          highlightedPartId={highlightedPartId}
          onHighlightPart={setHighlightedPartId}
          onExport={exportNestFile}
        />
      )
    }
    const parts = activeTab.replace('group-', '')
    const groupId = parts.startsWith('idx-')
      ? null
      : parseInt(parts)
    const idx = parts.startsWith('idx-')
      ? parseInt(parts.replace('idx-', ''))
      : visibleGroups.findIndex(g => g.material_group_id === groupId)
    const group = visibleGroups[idx]
    if (!group) {
      return <div style={{ padding: 20, color: colors.textMuted }}>Group not found in current nest.</div>
    }
    const mg = materialGroups.find(m => m.group_id === group.material_group_id)
    return (
      <NesterGroupDetail
        colors={colors}
        mode={mode}
        group={group}
        mg={mg}
        idx={idx}
        highlightedPartId={highlightedPartId}
        onHighlightPart={setHighlightedPartId}
        onExport={exportNestFile}
      />
    )
  }
}

function buildProjectPayload(f) {
  return {
    project_name: f.projectName,
    customer_name: f.customerName || null,
    customer_id: f.customerId ? parseInt(f.customerId) : null,
    description: f.description || null,
    status: f.status,
    surface_finish: f.surfaceFinish || null,
    material_type: f.materialType || null,
    thickness_display: f.thicknessDisplay || null,
    thickness_in: f.thicknessIn ? parseFloat(f.thicknessIn) : null,
    sheet_width: f.sheetWidth ? parseFloat(f.sheetWidth) : null,
    sheet_length: f.sheetLength ? parseFloat(f.sheetLength) : null,
    feedrate_ipm: f.feedrateIpm ? parseFloat(f.feedrateIpm) : null,
    pierce_time_sec: f.pierceTimeSec ? parseFloat(f.pierceTimeSec) : null,
    gas_type: f.gasType || null,
    nozzle_clean_pierces: f.nozzleCleanPierces ? parseInt(f.nozzleCleanPierces) : null,
    nozzle_clean_sec: f.nozzleCleanSec ? parseFloat(f.nozzleCleanSec) : null,
    customer_supplied_material: f.customerSupplied,
    cost_per_sheet: f.customerSupplied ? null : (f.costPerSheet ? parseFloat(f.costPerSheet) : null),
    cost_per_lb: f.customerSupplied ? null : (f.costPerLb ? parseFloat(f.costPerLb) : null),
    gas_flow_cfh: f.gasFlowCfh ? parseFloat(f.gasFlowCfh) : null,
    gas_cost_per_cf: f.gasCostPerCf ? parseFloat(f.gasCostPerCf) : null,
    consumable_rate_per_hour: f.consumableRate ? parseFloat(f.consumableRate) : null,
  }
}

const labelStyle = (c) => ({
  display: 'flex', alignItems: 'center', gap: 4,
  fontSize: 11, color: c.text,
})
const miniInput = (c) => ({
  width: 56,
  padding: '3px 6px', fontSize: 12,
  border: `1px solid ${c.border}`, borderRadius: 3,
  background: c.panel, color: c.text,
})
const kbd = (c) => ({
  display: 'inline-block',
  padding: '2px 6px', borderRadius: 3,
  border: `1px solid ${c.border}`, background: c.panelAlt,
  color: c.text, fontSize: 11, fontWeight: 700,
  fontFamily: 'ui-monospace, SFMono-Regular, Consolas, monospace',
  minWidth: 18, textAlign: 'center',
})
